import { NgOptimizedImage } from '@angular/common';
import {
  AfterViewInit,
  ChangeDetectionStrategy,
  Component,
  ElementRef,
  OnDestroy,
  inject,
  input,
} from '@angular/core';
import '@preline/carousel';
import type { ICarousel, ICarouselOptions } from '@preline/carousel';

type HSCarouselConstructor = new (element: HTMLElement, options?: ICarouselOptions) => ICarousel;

declare global {
  interface Window {
    HSCarousel?: HSCarouselConstructor;
  }
}

export type CarouselImage = Readonly<{
  src: string;
  alt: string;
}>;

@Component({
  selector: 'app-image-carousel',
  templateUrl: './image-carousel.html',
  styleUrl: './image-carousel.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [NgOptimizedImage],
  host: {
    class: 'block',
  },
})
export class ImageCarousel implements AfterViewInit, OnDestroy {
  readonly images = input.required<readonly CarouselImage[]>();
  readonly isAutoPlay = input<boolean>(true);
  readonly isInfiniteLoop = input<boolean>(true);
  readonly autoPlayIntervalMs = input<number>(4000);

  private readonly host = inject(ElementRef<HTMLElement>);
  private carousel: ICarousel | null = null;

  ngAfterViewInit(): void {
    const element = this.getElement();
    const carouselConstructor = window.HSCarousel;

    if (!element || !carouselConstructor) {
      return;
    }

    this.carousel = new carouselConstructor(element, this.getOptions());
  }

  ngOnDestroy(): void {
    if (!this.carousel) {
      return;
    }

    try {
      const el = this.getElement();
      if (el) {
        this.carousel.destroy();
      } else {
        try {
          this.carousel.destroy();
        } catch (err) {
          console.warn('HSCarousel.destroy() failed during component teardown', err);
        }
      }
    } catch (err) {
      console.warn('Error while destroying HSCarousel instance', err);
    } finally {
      this.carousel = null;
    }
  }

  private getElement(): HTMLElement | null {
    const element = this.host.nativeElement.querySelector('[data-hs-carousel]');

    return element instanceof HTMLElement ? element : null;
  }

  private getOptions(): ICarouselOptions {
    return {
      currentIndex: 0,
      isAutoPlay: this.isAutoPlay(),
      isInfiniteLoop: this.isInfiniteLoop(),
      speed: this.autoPlayIntervalMs(),
      updateDelay: 100,
    };
  }
}
